/**
 * Snackbar Notifications
 * -----------------------------------------------------------------------------
 *
 * Snackbar notifications are also known as "Toast" notifications.
 *
 * This as a very basic implementation of a snackbar notification. Some
 * snackbars allow the user to click on an action/link in the notification
 * and/or dismiss it. These features are not a part of this implementation.
 *
 * Snackbar Life Cycle
 *
 *   1. An event should occur, like a `click`.
 *   2. Then there might be a process/action that needs to occur; ajax request,
 *      copy to clipboard, etc.
 *   3. If #2, and successful, instantiate snackbar. If not #2, define message
 *      to user, instantiate snackbar.
 *   4. Snackbar does the rest.
 */

// Imports
const $ = require( 'jquery' );



/**
 * Snackbar "Interface"
 *
 * Snackbar notifications emit two events that can be hooked into. Currently,
 * there is no obvious use case for them but are provided in case they are
 * needed/useful. Snackbar `options` are sent with the event.
 *
 * @event show.und.snackbar
 * @event hide.und.snackbar
 *
 * @param {Object} options See `Snackbar.Defaults` for a list of configuration
 *                         options. The only required option is `message`.
 */
function Snackbar( options ) {

  // Globals
  const _options  = Object.assign( {}, Snackbar.Defaults, options );
  const $snackbar = $( _options.container );



  /**
   * Reset snackbar to initial state.
   *
   * Notes
   * 1. We want that data passed with the `hide` event to reference the options
   *    of the notification being hidden, not the notification now being
   *    presented. So we get the `_options` data stored from the previous call
   *    instead of `_options` from this call.
   */
  function _resetSnackbar() {

    $snackbar
      .trigger(
        Snackbar.Event.HIDE,
        $snackbar.data( Snackbar.DATA_KEY ) /* 1 */
      )
      .removeClass( Snackbar.ClassName.SNACKBAR_SHOW )
      .empty()
      .removeData( Snackbar.DATA_KEY );

  }

  /**
   * Open/Instantiate Snackbar
   *
   * Notes
   * 1. First, check to see if a snackbar is currently instantiated. If so,
   *    reset it before creating the new snackbar.
   */
  function _open() {

    /* 1 */
    if ( Snackbar._timeoutID ) {

      clearTimeout( Snackbar._timeoutID );
      _resetSnackbar();

      Snackbar._timeoutID = null;

    }

    $snackbar
      .data( Snackbar.DATA_KEY, _options )
      .trigger( Snackbar.Event.SHOW, _options )
      .text( _options.message )
      .addClass( Snackbar.ClassName.SNACKBAR_SHOW );

    Snackbar._timeoutID = setTimeout( _resetSnackbar, _options.duration );

  }



  // Instantiate Snackbar.
  _open();

}



/**
 * Snackbar default options.
 *
 * @type {Object}
 *
 * @property {String} container Optional. A CSS selector pointing to a DOM
 *                              element that will be used to present the
 *                              notification to the user. Only use a different
 *                              container when absolutely necessary.
 * @property {Integer} duration Optional. How long the snackbar notification
 *                              should be presented to the user, in
 *                              milliseconds. Default is `3` seconds.
 * @property {String} message   Required. Message to insert into the snackbar
 *                              container. Displays default message of
 *                              `[undefined]` to remind developer to define a
 *                              message.
 */
Snackbar.Defaults = {
  container: '#snackbar',
  duration:   3000,
  message:   '[undefined]',
};

/**
 * Property name for plugin instance store with jQuery `data()`.
 *
 * @type {String}
 */
Snackbar.DATA_KEY = 'und.snackbar';

/**
 * Event namespace for plugin.
 *
 * @type {String}
 */
Snackbar.NAMESPACE = `.${Snackbar.DATA_KEY}`;

/**
 * Custom Events
 *
 * @type {Object}
 */
Snackbar.Event = {
  HIDE: `hide${ Snackbar.NAMESPACE }`,
  SHOW: `show${ Snackbar.NAMESPACE }`
};

/**
 * Plugin class names.
 *
 * @type {Object}
 */
Snackbar.ClassName = {
  SNACKBAR_SHOW: 'snackbar--show'
};

/**
 * `setTimout()` ID.
 *
 * This variables needs to exist outside of each `Snackbar()` call.
 *
 * @private
 * @type {Integer}
 */
Snackbar._timeoutID;



module.exports = Snackbar;
